import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { ArrowLeft, Users, Copy, Check, Loader2, Hash, MessageSquare, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadResult = () => {
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState(null);
  const [editedSummary, setEditedSummary] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referenceId, setReferenceId] = useState(null);

  useEffect(() => {
    // Retrieve the lead result from sessionStorage
    const stored = sessionStorage.getItem('leadResult');
    if (stored) {
      const data = JSON.parse(stored);
      setLeadData(data);
      setEditedSummary(data.generatedSummary || '');
    } else {
      // No data, redirect back
      navigate('/tradie/leads-referral');
    }
  }, [navigate]);

  const handleCopy = async () => {
    if (!editedSummary) return;
    
    const textArea = document.createElement('textarea');
    textArea.value = editedSummary;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, editedSummary.length);
    
    let success = false;
    
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }
    
    if (!success && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(editedSummary);
        success = true;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Could not copy. Try selecting the text manually.');
    }
  };

  const handleSave = async () => {
    if (!editedSummary.trim()) {
      toast.error('Summary cannot be empty');
      return;
    }

    setSaving(true);
    
    try {
      // Create a lead record via dedicated endpoint
      const payload = {
        entry_type: leadData.entry_type,
        entry_type_display: leadData.entryTypeDisplay,
        work_type: leadData.work_type,
        work_type_display: leadData.workTypeDisplay,
        client_name: leadData.client_name,
        contact_person: leadData.contact_person || leadData.main_contact,
        phone: leadData.phone,
        email: leadData.email,
        additional_contacts: leadData.additional_contacts,
        site_location: leadData.site_location,
        timing: leadData.timing,
        follow_up_by: leadData.follow_up_by,
        estimated_value: leadData.estimated_value,
        project_notes: leadData.project_notes,
        source: leadData.source,
        source_display: leadData.sourceDisplay,
        referral_approach: leadData.referral_approach,
        referral_approach_display: leadData.referralApproachDisplay,
        referral_notes: leadData.referral_notes,
        raw_notes: leadData.raw_notes,
        summary_text: editedSummary
      };

      const response = await axios.post(`${API_URL}/leads`, payload);
      
      setReferenceId(response.data.reference_id);
      toast.success(`Lead saved! Reference: ${response.data.reference_id}`);
      
      // Clear sessionStorage
      sessionStorage.removeItem('leadResult');
      
      // Navigate to My Updates
      navigate('/tradie/my-updates');
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!leadData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20 page-enter">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="h-1 bg-emerald-500 absolute top-0 left-0 right-0"></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-btn"
              onClick={() => navigate('/tradie/leads-referral')}
              className="text-white hover:bg-slate-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Trophy className="w-7 h-7 text-emerald-400" />
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">Lead Summary</h1>
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">WIN WORK</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="win" />
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="win" activeModule="lead" />

      {/* Content */}
      <main className="p-4 max-w-lg mx-auto pb-8">
        <Card className="border-2 border-slate-200 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                {leadData.entryTypeDisplay || 'Lead'}
                {leadData.workTypeDisplay && (
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full ml-1">
                    {leadData.workTypeDisplay}
                  </span>
                )}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                data-testid="copy-summary-btn"
                onClick={handleCopy}
                className={copied ? "bg-green-100 border-green-300" : ""}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Quick info badges */}
            <div className="flex flex-wrap gap-2 mt-2">
              {leadData.sourceDisplay && (
                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                  Source: {leadData.sourceDisplay}
                </span>
              )}
              {leadData.client_name && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                  {leadData.client_name}
                </span>
              )}
              {leadData.estimated_value && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                  {leadData.estimated_value}
                </span>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Editable Summary */}
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-2 block">
                Structured Summary
              </label>
              <Textarea
                data-testid="summary-textarea"
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                className="min-h-[350px] text-sm text-slate-700 leading-relaxed bg-slate-50 border-slate-200 resize-y font-sans"
                placeholder="Summary will appear here..."
              />
              <p className="text-xs text-slate-400 mt-1">
                Edit the summary above before saving if needed
              </p>
            </div>

            {/* Reference ID info */}
            {referenceId && (
              <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Hash className="w-4 h-4 text-purple-500" />
                <span className="text-sm font-semibold text-purple-700">Reference: {referenceId}</span>
              </div>
            )}

            {/* Actions */}
            <div className="pt-2 space-y-3">
              <Button
                data-testid="copy-full-btn"
                onClick={handleCopy}
                variant="outline"
                className="w-full h-12 border-2"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy Summary'}
              </Button>
              
              <Button
                data-testid="save-btn"
                onClick={handleSave}
                disabled={saving}
                className="w-full h-12 bg-purple-500 hover:bg-purple-600 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving Lead...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save & Done
                  </>
                )}
              </Button>
            </div>

            {/* Footer Note */}
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  Want a signature added, PDF, email issued or custom setup? Talk to our team about adding more capability to your 2Tap setup.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      
      <FooterNav />
    </div>
  );
};

export default LeadResult;
